import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { FormHelperText, InputLabel, Tooltip, Typography } from "@mui/material";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';


import RadioButtonsGroup from "../../../components/RadioButton";
import DatePicker from "../../../components/DatePicker";
import FileUpload from "../../../components/FileUpload";
import Input from "../../../components/Input";
import Select from "../../../components/Select";
import Button from "../../../components/Button";
import Modal from "../../../components/Modal";
import Upload from "../../../components/Upload";
import messages from "../../../assets/locale/messages";
import {
  ARABIC_REGEX,
  ENGLISH_REGEX,
  NAME_REGEX,
  ONE_SPACE_ONLY,
  POSITIVE_FLOAT_REGEX,
  ARABIC_DIACRITICS_SPACE_DASH_SINGLE_QUOTE_AND_NUMBERS,
  ARABIC_DIACRITICS_SPACE_DASH_SINGLE_QUOTE_AND_NUMBERS_ATLEAST_TWO_ARABIC_CHARACTERS,
  ENGLISH_SPACE_DASH_SINGLE_QUOTE_AND_NUMBERS_ATLEAST_TWO_ENGLISH_CHARACTERS,
  ENGLISH_SPACE_DASH_SINGLE_QUOTE_AND_NUMBERS,
  ONLY_ENGLISH_SPACE_DOT_COMMA_SEMICOLON,
  ONLY_ARABIC_SPACE_DOT_COMMA_SEMICOLON,
} from "../../../utils/Patterns";
import {
  directories,
  generateUniqueFileName,
  uploadToS3,
} from "../../../utils/Aws-S3";
import { mimarCommissionList } from "../../../utils/Constants";
import { setPageMainTitle } from "../../../utils/Helpers";
import {
  addProviderRequest,
  editProviderRequest,
  getProviderDetailsRequest,
  getProviderDetailsResponse,
} from "../../../store/Providers/actions";
import { getCategoriesRequest } from "../../../store/Categories/actions";

import "./AddEditServiceProviders.scss";

const AddEditServiceProviders = () => {
  const dispatch = useDispatch();
  const { id } = useParams();
  const [imgBanner, setImgBanner] = useState();
  const [imgBannerChanged, setImgBannerChanged] = useState(false);
  const [imgLogo, setImgLogo] = useState();
  const [imgLogoChanged, setImgLogoChanged] = useState(false);
  const [imgCRFileName, setImgCRFileName] = useState("");
  const [imgContractFileName, setImgContractFileName] = useState("");
  const [CRFileFormatErrMsg, setCRFileFormatErrMsg] = useState('');
  const [contractFileFormatErrMsg, setContractFileFormatErrMsg] = useState('');
  const [CRUrl, setCRUrl] = useState('');
  const [contractUrl, setContractUrl] = useState('');
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [isOpenCommissionModal, setIsOpenCommissionModal] = useState(false);
  const providerDetails = useSelector(
    (state) => state.providers.providerDetails
  );
  const categoryList = useSelector((state) => state.categories.list);
  const lang = useSelector((state) => state.locale.lang);
  const { shared, serviceProviders, pagesTitles, inputsValidations } = messages[lang];
  const mimarCommissionOptions = [
    {
      id:mimarCommissionList.percentage,
      label:serviceProviders.addServiceProvider.mimarCommissionOptions.percentage,
      value:mimarCommissionList.percentage
    },
    {
      id:mimarCommissionList.fixed_fees,
      label:serviceProviders.addServiceProvider.mimarCommissionOptions.fixed_fees,
      value:mimarCommissionList.fixed_fees
    }
  ]
    
  const {
    values,
    setFieldValue,
    setValues,
    handleSubmit,
    touched,
    errors,
    setFieldTouched,
    dirty,
    isValid,
    setFieldError
  } = useFormik({
    initialValues: id
      ? {
          name_en: "",
          name_ar: "",
          description_en:"",
          description_ar:"",
          category: null,
          banner_image: null,
          logo_image: null,
          commission_type:mimarCommissionList.percentage,
          mimars_commission:"0",
          commission_fixed_fees:"0",
          commercial_registration_number:'',
          expiry_date:"",
          upload_CR:null,
          joining_date:"",
          contract:null,
          iban:"",
        }
      : { 
          name_en:"",
          name_ar: "",
          email: "",
          mimars_commission:"0",
          commission_type:mimarCommissionList.percentage,
          commission_fixed_fees:"0",
          commercial_registration_number:'',
          expiry_date:"",
          upload_CR:null,
          joining_date:"",
          contract:null,
          iban:"",
        },
    validationSchema: id
      ? Yup.object({
          name_en: Yup.string()
            .required("enNameRequired")
            .matches(ENGLISH_SPACE_DASH_SINGLE_QUOTE_AND_NUMBERS, "englishSpaceDashSingleQuoteAndNumbers")
            .matches(ENGLISH_SPACE_DASH_SINGLE_QUOTE_AND_NUMBERS_ATLEAST_TWO_ENGLISH_CHARACTERS, "atleastTwoEnglishCharacters")
            .max(100,"maximumChars100"),
          name_ar: Yup.string()
            .required("arNameRequired")
            .matches(ARABIC_DIACRITICS_SPACE_DASH_SINGLE_QUOTE_AND_NUMBERS, "arabicDiacriticsSpaceDashSingleQuoteAndNumbers")
            .matches(ARABIC_DIACRITICS_SPACE_DASH_SINGLE_QUOTE_AND_NUMBERS_ATLEAST_TWO_ARABIC_CHARACTERS, "atleastTwoArabicCharacters")
            .max(100,"maximumChars100"),
          description_en: Yup.string()
            .optional()
            .matches(ONLY_ENGLISH_SPACE_DOT_COMMA_SEMICOLON, "onlyEnglishSpaceDotCommaSemicolon"),
          description_ar: Yup.string()
            .optional()
            .matches(ONLY_ARABIC_SPACE_DOT_COMMA_SEMICOLON, "onlyArabicSpaceDotCommaSemicolon"),
          category: Yup.object().nullable().required("categoryRequired"),
          banner_image: Yup.mixed().required("bannerRequired"),
          logo_image: Yup.mixed().required("logoRequired"),
          commission_type: Yup.string().required("commissionTypeRequired"),
          mimars_commission:Yup.string().matches(POSITIVE_FLOAT_REGEX,'positiveNumber')
          .when('commission_type', (commission_type, schema) => {
            if (commission_type === 'fixed_fees') {
              return schema.notRequired();
            }
            return schema.required('required');
          }),
          commission_fixed_fees:Yup.string().matches(POSITIVE_FLOAT_REGEX,'positiveNumber')
          .when('commission_type', (commission_type, schema) => {
            if (commission_type === 'percentage') {
              return schema.notRequired();
            }
            return schema.required('required');
          }),
          commercial_registration_number:Yup.string().nullable()
          .test(
            "all-or-nothing", 
            "required",
            function (value) {
              const { expiry_date, upload_CR } = this.parent;
              const anyFilled = value || expiry_date || upload_CR || imgCRFileName;
              return value || !anyFilled;
            }
          ),
          expiry_date:Yup.date().nullable()
          .test(
            "all-or-nothing",
            "required",
            function (value) {
              const { commercial_registration_number, upload_CR } = this.parent;
              const anyFilled = value || commercial_registration_number || upload_CR || imgCRFileName;
              return value || !anyFilled;
            }
          ),
          upload_CR:Yup.mixed().nullable()
          .test(
            "all-or-nothing",
            "required",
            function (value) {
              const { commercial_registration_number, expiry_date } = this.parent;
              const anyFilled = value || commercial_registration_number || expiry_date;
              return value || !anyFilled || !!imgCRFileName;
            }
          ),
          joining_date:Yup.date().nullable(),
          contract:Yup.mixed().nullable(),
          iban:Yup.string().nullable()
        })
      : Yup.object({
          name_en: Yup.string()
            .required("enNameRequired")
            .matches(ENGLISH_SPACE_DASH_SINGLE_QUOTE_AND_NUMBERS, "englishSpaceDashSingleQuoteAndNumbers")
            .matches(ENGLISH_SPACE_DASH_SINGLE_QUOTE_AND_NUMBERS_ATLEAST_TWO_ENGLISH_CHARACTERS, "atleastTwoEnglishCharacters")
            .max(100,"maximumChars100"),
          name_ar: Yup.string()
            .required("arNameRequired")
            .matches(ARABIC_DIACRITICS_SPACE_DASH_SINGLE_QUOTE_AND_NUMBERS, "arabicDiacriticsSpaceDashSingleQuoteAndNumbers")
            .matches(ARABIC_DIACRITICS_SPACE_DASH_SINGLE_QUOTE_AND_NUMBERS_ATLEAST_TWO_ARABIC_CHARACTERS, "atleastTwoArabicCharacters")
            .max(100,"maximumChars100"),
          email: Yup.string()
            .email("validEmailFormat")
            .required("emailRequired"),
          commission_type: Yup.string().required("commissionTypeRequired"),
          mimars_commission:Yup.string().matches(POSITIVE_FLOAT_REGEX,'positiveNumber')
          .when('commission_type', (commission_type, schema) => {
            if (commission_type === 'fixed_fees') {
              return schema.notRequired();
            }
            return schema.required('required');
          }),
          commission_fixed_fees:Yup.string().matches(POSITIVE_FLOAT_REGEX,'positiveNumber')
          .when('commission_type', (commission_type, schema) => {
            if (commission_type === 'percentage') {
              return schema.notRequired();
            }
            return schema.required('required');
          }),
          commercial_registration_number:Yup.string().nullable()
          .test(
            "all-or-nothing",
            "required",
            function (value) {
              const { expiry_date, upload_CR } = this.parent;
              const anyFilled = value || expiry_date || upload_CR;
              return !!value || !anyFilled;
            }
          ),
          expiry_date:Yup.date().nullable()
          .test(
            "all-or-nothing",
            "required",
            function (value) {
              const { commercial_registration_number, upload_CR } = this.parent;
              const anyFilled = value || commercial_registration_number || upload_CR;
              return value || !anyFilled;
            }
          ),
          upload_CR:Yup.mixed().nullable()
          .test(
            "all-or-nothing",
            "required",
            function (value) {
              const { commercial_registration_number, expiry_date } = this.parent;
              const anyFilled = value || commercial_registration_number || expiry_date;
              return value || !anyFilled ;
            }
          ),
          joining_date:Yup.date().nullable(),
          contract:Yup.mixed().nullable(),
          iban:Yup.string().nullable()
        }),
    onSubmit: async ({
      name_en,
      name_ar,
      email,
      category,
      banner_image,
      logo_image,
      commission_type,
      commission_fixed_fees,
      mimars_commission,
      commercial_registration_number,
      expiry_date,
      upload_CR,
      joining_date,
      contract,
      iban
    }) => {
      if (!id) {
        let providerData = { 
          name_en,
          name_ar,
          email,
          commission_type,
          commission_fixed_fees: commission_fixed_fees==='' ? null : +commission_fixed_fees,
          mimars_commission: mimars_commission==='' ? null : +mimars_commission,
          commercial_registration_number : !!commercial_registration_number ? commercial_registration_number : null,
          commercial_registration_expiry_date:expiry_date,
          commercial_registration_file_url: !!imgCRFileName ? imgCRFileName : null,
          joining_date,
          contract: !!imgContractFileName ? imgContractFileName : null,
          iban
        };
        if (upload_CR) {
            const data = await uploadToS3(
              upload_CR,
              generateUniqueFileName(upload_CR.name),
              directories.providers_CR
            );
            providerData.commercial_registration_file_url = data?.link;
        }
        if (contract) {
            const data = await uploadToS3(
              contract,
              generateUniqueFileName(contract.name),
              directories.providers_contract
            );
            providerData.contract = data?.link;
        }
        dispatch(addProviderRequest(providerData));
      } else {
        if( commission_type !== providerDetails?.commission_type ||
          (mimars_commission !== providerDetails?.mimars_commission && commission_type === 'percentage') ||
          (commission_fixed_fees !== providerDetails?.commission_fixed_fees && commission_type === 'fixed_fees')){
            setIsOpenCommissionModal(true);
        }else{
          handleEditProvider();
        }
      }
    },
  });

  const handleEditProvider = async () => {
    let { 
      name_en, 
      name_ar,
      description_en,
      description_ar,
      category,
      commission_type, 
      commission_fixed_fees, 
      mimars_commission, 
      commercial_registration_number, 
      expiry_date, 
      upload_CR, 
      joining_date, 
      contract, 
      iban,
      banner_image,
      logo_image,
    } = values;
    let providerData = { 
      name_en,
      name_ar, 
      description_en: description_en.trim() || '',
      description_ar: description_ar.trim() || '',
      category_id: category.id, 
      commission_type,
      commission_fixed_fees: commission_fixed_fees==='' ? null : +commission_fixed_fees,
      mimars_commission: mimars_commission==='' ? null : +mimars_commission,
      commercial_registration_number : !!commercial_registration_number ? commercial_registration_number : null,
      commercial_registration_expiry_date:expiry_date,
      commercial_registration_file_url: !!imgCRFileName && !!providerDetails?.commercial_registration_file_url ? providerDetails?.commercial_registration_file_url : null,
      contract: !!imgContractFileName && !!providerDetails?.contract ? providerDetails?.contract : null,
      joining_date,
      iban
    };
    if (banner_image) {
      if (imgBannerChanged) {
        const data = await uploadToS3(
          banner_image,
          generateUniqueFileName(banner_image.name),
              directories.providers_banners
            );
            providerData.banner_image = data?.link;
          } else {
            providerData.banner_image = banner_image;
          }
    }
    if (logo_image) {
      if (imgLogoChanged) {
        const data = await uploadToS3(
          logo_image,
          generateUniqueFileName(logo_image.name),
              directories.providers_logos
            );
            providerData.logo_image = data?.link;
          } else {
            providerData.logo_image = logo_image;
          }
    }
    if (upload_CR) {
      const data = await uploadToS3(
        upload_CR,
        generateUniqueFileName(upload_CR.name),
        directories.providers_CR
      );
      providerData.commercial_registration_file_url = data?.link;
    }
    if (contract) {
      const data = await uploadToS3(
        contract,
        generateUniqueFileName(contract.name),
        directories.providers_contract
      );
      providerData.contract = data?.link;
    }
    dispatch(
      editProviderRequest({
        id,
        data: providerData,
      })
    );
  };

  useEffect(() => {
    setPageMainTitle("serviceProvidersManagement");
    getCategoriesDropDownList();
  }, []);

  useEffect(() => {
    setCategoryOptions(
      categoryList?.map((option) => ({
        id: option.id,
        label: lang === "en" ? option.title_en : option.title_ar,
      }))
    );
  }, [categoryList, lang]);

  useEffect(() => {
    if (values["category"]) {
      setFieldValue(
        "category",
        categoryOptions.find((option) => option.id === values["category"].id)
      );
    }
  }, [lang, categoryOptions]);

  useEffect(() => {
    if (id) {
      dispatch(getProviderDetailsRequest(id));
    }
    return () => {
      dispatch(getProviderDetailsResponse({}));
    };
  }, [dispatch, id]);

  useEffect(() => {
    if (id && providerDetails) {
      setFieldTouched({
        name_en: true,
        name_ar: true,
        description_en: true,
        description_ar: true,
        email: true,
        category: providerDetails?.category ? true : false,
        banner_image: true,
        logo_image: true,
        mimars_commission:providerDetails?.mimars_commission ? true : false,
        commission_type:true,
        commission_fixed_fees:providerDetails?.commission_fixed_fees ? true : false,
        expiry_date: !!providerDetails?.commercial_registration_expiry_date,
        commercial_registration_number: !!providerDetails?.commercial_registration_number,
        upload_CR:!!providerDetails?.commercial_registration_file_url,
        contract:!!providerDetails?.contract,
        joining_date: !!providerDetails?.joining_date,
        iban: !!providerDetails?.iban,
      });
      setCRUrl(providerDetails?.commercial_registration_file_url)
      setContractUrl(providerDetails?.contract)
      setImgCRFileName(providerDetails?.commercial_registration_file_url?.split("/")?.slice(-1)[0]?.split("_")?.slice(-1)[0])
      setImgContractFileName(providerDetails?.contract?.split("/")?.slice(-1)[0]?.split("_")?.slice(-1)[0]);
      setValues({
        name_en: providerDetails.name_en,
        name_ar: providerDetails.name_ar,
        description_en: providerDetails.description_en || '',
        description_ar: providerDetails.description_ar || '',
        email: providerDetails.email,
        banner_image: providerDetails.banner_image,
        logo_image: providerDetails.logo_image,
        category: providerDetails?.category
          ? {
              id: providerDetails.category?.id,
              label: lang === 'en' ? providerDetails.category?.name_en : providerDetails.category?.name_ar,
            }
          : null,
        mimars_commission: providerDetails?.mimars_commission === null ? '' : providerDetails?.mimars_commission,
        commission_type:providerDetails?.commission_type,
        commission_fixed_fees:providerDetails?.commission_fixed_fees === null ? '' : providerDetails?.commission_fixed_fees,
        commercial_registration_number: providerDetails?.commercial_registration_number,
        expiry_date:providerDetails?.commercial_registration_expiry_date,
        joining_date: providerDetails?.joining_date,
        iban: providerDetails?.iban,
      });
    }
  }, [providerDetails, id]);

  useEffect(()=>{
    if(!!providerDetails?.commercial_registration_file_url){
      setFieldTouched("upload_CR");
    }
  },[imgCRFileName]);

  const getCategoriesDropDownList = () => {
    dispatch(
      getCategoriesRequest({
        page: -1,
      })
    );
  };

  const onChangeCRFile = (value) => {
    if(!providerDetails?.commercial_registration_file_url){
      setFieldTouched("expiry_date");
      setFieldTouched("upload_CR");
      setFieldTouched("commercial_registration_number");
    }
    if(value){
      const fileSize = value.size / (1024 * 1024); // Convert to MB
      const allowedTypes = ['.pdf', '.png', '.jpeg', '.jpg'];
      const fileExt = value.name.toLowerCase().substring(value.name.lastIndexOf('.'));
          
      if (!allowedTypes.includes(fileExt)) {
        setCRFileFormatErrMsg("acceptedFilesImgPdf")
        setFieldValue("upload_CR",value);
        setImgCRFileName("");
      }else if (fileSize > 5) {
        setCRFileFormatErrMsg("acceptedSize")
        setFieldValue("upload_CR",value);
        setImgCRFileName("");
      } else {
        setCRFileFormatErrMsg("");
        setFieldValue("upload_CR", value);
        setImgCRFileName("");
      }
      } else {
        setCRUrl("");
        setImgCRFileName("");
        setCRFileFormatErrMsg("");
        setFieldValue("upload_CR",null)
      };
  };

  const onChangeContractFile = (value) => {
    if(!providerDetails?.contract_file_url){setFieldTouched("contract")}
    if(value){
      const fileSize = value.size / (1024 * 1024); // Convert to MB
      const allowedTypes = ['.pdf', '.png', '.jpeg', '.jpg'];
      const fileExt = value.name.toLowerCase().substring(value.name.lastIndexOf('.'));
          
      if (!allowedTypes.includes(fileExt)) {
        setContractFileFormatErrMsg("acceptedFilesImgPdf")
        setFieldValue("contract",value);
        setImgContractFileName("");
      }else if (fileSize > 5) {
        setContractFileFormatErrMsg("acceptedSize")
        setFieldValue("contract",value);
        setImgContractFileName("");
      } else {
        setContractFileFormatErrMsg("");
        setFieldValue("contract", value);
        setImgContractFileName("");
      }
      } else {
        setContractUrl("");
        setImgContractFileName("");
        setContractFileFormatErrMsg("");
        setFieldValue("contract",null)
      };
  };


  return (
    <div className="add-edit-service-providers-page"> 
      <div className="d-flex mb-4">
        <Typography variant="h4" sx={{ flexGrow: 1 }} className={`page-title`}>
          {id
            ? pagesTitles.serviceProvidersEdit
            : pagesTitles.serviceProvidersAdd}
        </Typography>
      </div>
      <form noValidate onSubmit={handleSubmit}>
        <div className="px-2 px-md-5 bg-white rounded-4 pt-4">
          {id && (
            <>
              <div className="row mt-4 flex-column-reverse flex-md-row">
                <div className="col-12 col-md-6 mt-4 mt-md-0">
                  <span className="text-dark font-medium">
                    {
                      serviceProviders.addServiceProvider.placeholders
                        .uploadLogo
                    }
                    <span className="err-asterisk">*</span>
                  </span>
                  <Tooltip
                    title={
                      <span className="fsize-12">{shared.filesAccepted} </span>
                    }
                  >
                    <span className="text-bg-primary-mid fsize-14 font-reguler ms-2">
                      {shared.acceptedFormatLabel}
                    </span>
                  </Tooltip>
                  <Upload
                    id="logo_image"
                    label={
                      <div className="mt-2 pt-1 d-flex flex-column align-items-center">
                        <ImageOutlinedIcon fontSize="large" />
                        <span>
                          {
                            serviceProviders.addServiceProvider.placeholders
                              .uploadLogo
                          }
                        </span>
                      </div>
                    }
                    onChange={(img, value) => {
                      setImgLogo(img);
                      setImgLogoChanged(true);
                      setFieldValue("logo_image", value);
                    }}
                    img={imgLogo || values["logo_image"]}
                    name="logo_image"
                    required={true}
                    isInputHasErr={
                      !!(touched["logo_image"] && errors["logo_image"])
                    }
                    errMsg={errors["logo_image"]}
                  />
                </div>
                <div className="col-12 col-md-6">
                  <span className="text-dark font-medium">
                    {serviceProviders.addServiceProvider.placeholders.banner}
                    <span className="err-asterisk">*</span>
                  </span>
                  <Tooltip
                    title={
                      <span className="fsize-12">{shared.filesAccepted} </span>
                    }
                  >
                    <span className="text-bg-primary-mid fsize-14 font-reguler ms-2">
                      {shared.acceptedFormatLabel}
                    </span>
                  </Tooltip>
                  <Upload
                    id="banner_image"
                    label={
                      <div className="pt-1 d-flex flex-column align-items-center justify-content-center banner-image-upload-initial-container px-5">
                        <ImageOutlinedIcon fontSize="large" />
                        <span>
                          {
                            serviceProviders.addServiceProvider.placeholders
                              .banner
                          }
                        </span>
                      </div>
                    }
                    onChange={(img, value) => {
                      setImgBanner(img);
                      setImgBannerChanged(true);
                      setFieldValue("banner_image", value);
                    }}
                    img={imgBanner || values["banner_image"]}
                    name="image"
                    isBanner={true}
                    className="mt-3 min-height-image"
                    bannerClassName="banner-image-upload-container"
                    required={true}
                  />
                </div>
              </div>

              
            </>
          )}
          <div className="row mt-4">
                <div className="col-12 col-md-6">
                  <Input
                    id="name_en"
                    label={shared.lists.enName}
                    placeholder={
                      serviceProviders.addServiceProvider.placeholders.EnName
                    }
                    name="name_en"
                    type="text"
                    required
                    onChange={(value) => {
                      if (ONE_SPACE_ONLY.test(value) || !value) {
                        setFieldTouched("name_en");
                        setFieldValue("name_en", value);
                      }
                    }}
                    value={values["name_en"]}
                    inputWrapperClass="mb-4"
                    labelClassName="text-dark font-medium mb-2"
                    isInputHasErr={!!(touched["name_en"] && errors["name_en"])}
                    errMsg={errors["name_en"]}
                  />
                </div>
                <div className="col-12 col-md-6">
                  <Input
                    id="name_ar"
                    label={shared.lists.arName}
                    name="name_ar"
                    type="text"
                    placeholder={
                      serviceProviders.addServiceProvider.placeholders.ArName
                    }
                    required
                    onChange={(value) => {
                      if (ONE_SPACE_ONLY.test(value) || !value) {
                        setFieldTouched("name_ar");
                        setFieldValue("name_ar", value);
                      }
                    }}
                    value={values["name_ar"]}
                    inputWrapperClass="mb-4"
                    labelClassName="text-dark font-medium mb-2"
                    isInputHasErr={!!(touched["name_ar"] && errors["name_ar"])}
                    errMsg={errors["name_ar"]}
                  />
                </div>
          </div>
          <div className="row mt-4">
            {id && (
              <>  
                <div className="col-12 col-md-6">
                <Input
                id="description_en"
                label={serviceProviders.addServiceProvider.labels.enDescription}
                placeholder={serviceProviders.addServiceProvider.placeholders.enDescription}
                name="description_en"
                type="text"
                multiline
                inputProps={{ maxLength: 125 }}
                inputClass='p-14'
                onChange={(value) => {
                  setFieldTouched('description_en');
                  setFieldValue('description_en', value);
                }}
                value={values['description_en']}
                inputWrapperClass="mb-4"
                labelClassName="text-dark fsize-16 fweight-400 mb-2"
                isInputHasErr={!!(touched['description_en'] && errors['description_en'])}
                errMsg={errors['description_en']}
              />
                </div>
                <div className="col-12 col-md-6">
                <Input
                id="description_ar"
                label={serviceProviders.addServiceProvider.labels.arDescription}
                name="description_ar"
                type="text"
                placeholder={serviceProviders.addServiceProvider.placeholders.arDescription}
                multiline
                inputProps={{ maxLength: 125 }}
                inputClass='p-14'
                onChange={(value) => {
                  setFieldTouched('description_ar');
                  setFieldValue('description_ar', value);
                }}
                value={values['description_ar']}
                inputWrapperClass="mb-4"
                labelClassName="text-dark fsize-16 fweight-400 mb-2"
                isInputHasErr={!!(touched['description_ar'] && errors['description_ar'])}
                errMsg={errors['description_ar']}
              />
                </div>
                </>
            )}
          </div>

          <div className="row mt-0 mt-md-4">
            {id && (
              <>  
                <div className="">
                <Input
                id="description_en"
                label={serviceProviders.addServiceProvider.labels.enDescription}
                placeholder={serviceProviders.addServiceProvider.placeholders.enDescription}
                name="description_en"
                type="text"
                multiline
                inputProps={{ maxLength: 125 }}
                inputClass='p-14'
                onChange={(value) => {
                  setFieldTouched('description_en');
                  setFieldValue('description_en', value);
                }}
                value={values['description_en']}
                inputWrapperClass="mb-4"
                labelClassName="text-dark fsize-16 fweight-400 mb-2"
                isInputHasErr={!!(touched['description_en'] && errors['description_en'])}
                errMsg={errors['description_en']}
              />
                </div>
                </>
            )}
          </div>
          <div className="row mt-0 mt-md-4">
            {id && (
              <>  
                <div className="">
                <Input
                id="description_ar"
                label={serviceProviders.addServiceProvider.labels.arDescription}
                name="description_ar"
                type="text"
                placeholder={serviceProviders.addServiceProvider.placeholders.arDescription}
                multiline
                inputProps={{ maxLength: 125 }}
                inputClass='p-14'
                onChange={(value) => {
                  setFieldTouched('description_ar');
                  setFieldValue('description_ar', value);
                }}
                value={values['description_ar']}
                inputWrapperClass="mb-4"
                labelClassName="text-dark fsize-16 fweight-400 mb-2"
                isInputHasErr={!!(touched['description_ar'] && errors['description_ar'])}
                errMsg={errors['description_ar']}
              />
                </div>
                </>
            )}
          </div>

          <div className="row mt-0 mt-md-4">
            {id && (
              <>  
                <div className="">
                <Input
                id="description_en"
                label={serviceProviders.addServiceProvider.labels.enDescription}
                placeholder={serviceProviders.addServiceProvider.placeholders.enDescription}
                name="description_en"
                type="text"
                multiline
                inputProps={{ maxLength: 125 }}
                inputClass='p-14'
                onChange={(value) => {
                  setFieldTouched('description_en');
                  setFieldValue('description_en', value);
                }}
                value={values['description_en']}
                inputWrapperClass="mb-4"
                labelClassName="text-dark fsize-16 fweight-400 mb-2"
                isInputHasErr={!!(touched['description_en'] && errors['description_en'])}
                errMsg={errors['description_en']}
              />
                </div>
                </>
            )}
          </div>
          <div className="row mt-0 mt-md-4">
            {id && (
              <>  
                <div className="">
                <Input
                id="description_ar"
                label={serviceProviders.addServiceProvider.labels.arDescription}
                name="description_ar"
                type="text"
                placeholder={serviceProviders.addServiceProvider.placeholders.arDescription}
                multiline
                inputProps={{ maxLength: 125 }}
                inputClass='p-14'
                onChange={(value) => {
                  setFieldTouched('description_ar');
                  setFieldValue('description_ar', value);
                }}
                value={values['description_ar']}
                inputWrapperClass="mb-4"
                labelClassName="text-dark fsize-16 fweight-400 mb-2"
                isInputHasErr={!!(touched['description_ar'] && errors['description_ar'])}
                errMsg={errors['description_ar']}
              />
                </div>
                </>
            )}
          </div>

          <div className="row mt-0 mt-md-4">
            {id && (
              <>  
                <div className="">
                <Input
                id="description_en"
                label={serviceProviders.addServiceProvider.labels.enDescription}
                placeholder={serviceProviders.addServiceProvider.placeholders.enDescription}
                name="description_en"
                type="text"
                multiline
                inputProps={{ maxLength: 125 }}
                inputClass='p-14'
                onChange={(value) => {
                  setFieldTouched('description_en');
                  setFieldValue('description_en', value);
                }}
                value={values['description_en']}
                inputWrapperClass="mb-4"
                labelClassName="text-dark fsize-16 fweight-400 mb-2"
                isInputHasErr={!!(touched['description_en'] && errors['description_en'])}
                errMsg={errors['description_en']}
              />
                </div>
                </>
            )}
          </div>
          <div className="row mt-0 mt-md-4">
            {id && (
              <>  
                <div className="">
                <Input
                id="description_ar"
                label={serviceProviders.addServiceProvider.labels.arDescription}
                name="description_ar"
                type="text"
                placeholder={serviceProviders.addServiceProvider.placeholders.arDescription}
                multiline
                inputProps={{ maxLength: 125 }}
                inputClass='p-14'
                onChange={(value) => {
                  setFieldTouched('description_ar');
                  setFieldValue('description_ar', value);
                }}
                value={values['description_ar']}
                inputWrapperClass="mb-4"
                labelClassName="text-dark fsize-16 fweight-400 mb-2"
                isInputHasErr={!!(touched['description_ar'] && errors['description_ar'])}
                errMsg={errors['description_ar']}
              />
                </div>
                </>
            )}
          </div>

          <div className="row mt-4">
            <div className="col-12 col-md-6">
              <Input
                id="email"
                label={shared.lists.email}
                placeholder={
                  serviceProviders.addServiceProvider.placeholders.email
                }
                name="email"
                type="text"
                required
                onChange={(value) => {
                  setFieldTouched("email");
                  setFieldValue("email", value);
                }}
                value={values["email"]}
                inputWrapperClass="mb-4"
                labelClassName="text-dark font-medium mb-2"
                isInputHasErr={!!(touched["email"] && errors["email"])}
                errMsg={errors["email"]}
                disabled={!!id}
              />
            </div>

            <div className="col-12 col-md-6">
              <DatePicker
                  name="joining_date"
                  labelClass="text-dark font-medium mb-2"
                  inputClass="w-100"
                  label={serviceProviders.addServiceProvider.labels.joiningDate}
                  placeholder={serviceProviders.addServiceProvider.placeholders.joiningDate}
                  value={values["joining_date"]}
                  onChange={(value) => {
                    setFieldTouched("joining_date");
                    setFieldValue("joining_date", value);
                  }}
                  isInputHasErr={!!(touched["joining_date"] && errors["joining_date"])}
                  errMsg={errors["joining_date"]}
                  placement="bottom"
                />
            </div>

          </div>

          <div className="row mt-4">
            <div className="col-12 col-md-6 mb-4">
                <Input
                  id="commercial_registration_number"
                  name="commercial_registration_number"
                  type="text"
                  labelClassName="text-dark font-medium mb-2"
                  label={serviceProviders.addServiceProvider.labels.CommercialRegistrationNumber}
                  placeholder={serviceProviders.addServiceProvider.placeholders.CommercialRegistrationNumber}
                  value={values["commercial_registration_number"]}
                  onChange={(value) => {
                    setFieldTouched("expiry_date");
                    setFieldTouched("upload_CR");
                    setFieldTouched("commercial_registration_number");
                    setFieldValue("commercial_registration_number", value);
                  }}
                  isInputHasErr={errors["commercial_registration_number"]}
                  errMsg={errors["commercial_registration_number"]}
                />
            </div>
            <div className="col-12 col-md-6 mb-4">
                <DatePicker
                  name="expiry_date"
                  labelClass="text-dark font-medium mb-2"
                  inputClass="w-100"
                  label={serviceProviders.addServiceProvider.labels.ExpiryDate}
                  placeholder={serviceProviders.addServiceProvider.placeholders.ExpiryDate}
                  value={values["expiry_date"]}
                  onChange={(value) => {
                    setFieldTouched("expiry_date");
                    setFieldTouched("upload_CR");
                    setFieldTouched("commercial_registration_number");
                    setFieldValue("expiry_date", value);
                  }}
                  isInputHasErr={errors["expiry_date"]}
                  errMsg={errors["expiry_date"]}
                  disablePast={true}
                  disableMinToday={true}
                  placement="bottom"
                />
            </div>
            <div className="col-12 col-md-6 mt-4">
                <FileUpload
                  id="upload_CR"
                  name="upload_CR"
                  label={serviceProviders.addServiceProvider.labels.UploadCR}
                  placeholder={serviceProviders.addServiceProvider.placeholders.UploadCR}
                  onChange={onChangeCRFile}
                  acceptFilesFormat=".pdf,image/png,image/jpeg,image/jpg"
                  touched={!!touched["upload_CR"]}
                  errMsg={CRFileFormatErrMsg || errors["upload_CR"]}
                  fileName={imgCRFileName || values["upload_CR"]?.name}
                  fileValue={values["upload_CR"]}
                  acceptedFormatlabel={<ErrorOutlineOutlinedIcon className="mb-1" />}
                  acceptedFormathint={serviceProviders.addServiceProvider.labels.acceptedFilesImgPdf}
                  previewUrl={CRUrl}
                />
            </div>
            <div className="col-12 col-md-6 mt-4">
              <FileUpload
                id="contract"
                name="contract"
                label={serviceProviders.addServiceProvider.labels.contract}
                placeholder={serviceProviders.addServiceProvider.placeholders.contract}
                onChange={onChangeContractFile}
                acceptFilesFormat=".pdf,image/png,image/jpeg,image/jpg"
                touched={!!touched["contract"]}
                errMsg={contractFileFormatErrMsg || errors["contract"]}
                fileName={imgContractFileName || values["contract"]?.name}
                fileValue={values["contract"]}
                acceptedFormatlabel={<ErrorOutlineOutlinedIcon className="mb-1"/>}
                acceptedFormathint={serviceProviders.addServiceProvider.labels.acceptedFilesImgPdf}
                previewUrl={contractUrl}
              />
            </div>
          </div>

          <div className={`row mt-4 ${id ? "" : "flex-row-reverse"}`}>
            {id && (
              <div className="col-12 col-md-6 mt-4">
                <Select
                  name="category"
                  labelClassName="text-dark font-medium mb-2"
                  label={shared.lists.categories}
                  options={categoryOptions}
                  inputClassName="input-wrapper"
                  placeholder={
                    serviceProviders.addServiceProvider.placeholders.category
                  }
                  required
                  value={values["category"]}
                  onChange={(value) => {
                    setFieldTouched("category");
                    setFieldValue("category", value);
                  }}
                  hasError={!!(touched["category"] && errors["category"])}
                  errMsg={errors["category"]}
                />
              </div>
          )}
            <div className="col-12 col-md-6 mt-4">
              <Input
                id="iban"
                label={serviceProviders.addServiceProvider.labels.iban}
                placeholder={serviceProviders.addServiceProvider.placeholders.iban}
                name="iban"
                type="text"
                onChange={(value) => {
                  setFieldTouched("iban");
                  setFieldValue("iban", value);
                }}
                value={values["iban"]}
                inputWrapperClass="mb-4"
                labelClassName="text-dark font-medium mb-2"
                isInputHasErr={!!(touched["iban"] && errors["iban"])}
                errMsg={errors["iban"]}
              />
            </div>
            <div className="col-12 col-md-6 mt-4">
              <InputLabel htmlFor="commission_type" className="text-dark font-medium mb-2">
                  <span className="font-medium text-dark">{shared.lists.mimarCommission} <span className="err-asterisk"> *</span></span>
                  <Tooltip
                      title={serviceProviders.CommissionTooltip}
                    >
                      <ErrorOutlineOutlinedIcon color="secondary" className="mx-1"/>
                  </Tooltip>
              </InputLabel>
              <RadioButtonsGroup
                value={values["commission_type"]}
                name="commission_type"
                labelId="commission_type"
                list={mimarCommissionOptions}
                onChange={(e) => {
                  setFieldTouched("commission_type");
                  setFieldValue("commission_type", e.target.value);
                  setFieldValue("commission_fixed_fees", '0');
                  setFieldValue("mimars_commission", '0');
                }}      
              />
              {values["commission_type"] === 'fixed_fees' &&
                <Input
                  id="commission_fixed_fees"
                  placeholder={serviceProviders.addServiceProvider.placeholders.commissionFixedFees}
                  name="commission_fixed_fees"
                  type="text"
                  onChange={(value) => {
                    setFieldTouched("commission_fixed_fees");
                    setFieldValue("commission_fixed_fees", value);
                  }}
                  value={values["commission_fixed_fees"]}
                  inputWrapperClass="mb-4"
                  labelClassName="text-dark font-medium mb-2"
                  isInputHasErr={!!(touched["commission_fixed_fees"] && errors["commission_fixed_fees"])}
                  errMsg={errors["commission_fixed_fees"]}
                />
              }
              {values["commission_type"] === 'percentage' &&
                <Input
                  id="mimars_commission"
                  placeholder={serviceProviders.addServiceProvider.placeholders.mimarPercentage}
                  name="mimars_commission"
                  type="text"
                  onChange={(value) => {
                    setFieldTouched("mimars_commission");
                    setFieldValue("mimars_commission", value);
                  }}
                  value={values["mimars_commission"]}
                  inputWrapperClass="mb-4"
                  labelClassName="text-dark font-medium mb-2"
                  isInputHasErr={!!(touched["mimars_commission"] && errors["mimars_commission"])}
                  errMsg={errors["mimars_commission"]}
                />
              }
              <FormHelperText
                error={!!(touched["commission_type"] && errors["commission_type"])}
                className={`font-medium fsize-12 ${lang === "en" ? "" : "text-end"}`}>
                {touched["commission_type"] && errors["commission_type"] && inputsValidations[errors["commission_type"]]}
              </FormHelperText>
            </div>

          </div>

          <div className="row mt-4 py-4 d-flex justify-content-center">
            {!id && (
              <div className="col-4">
                <Button
                  type="submit"
                  className="btn-secondary"
                  block
                  label={shared.save}
                  labelClass="px-4 fsize-16 font-medium text-white"
                  disabled={!dirty || !isValid || !!CRFileFormatErrMsg}
                />
              </div>
            )}
            {id && (
              <div className="col-4">
                <Button
                  onClick={() => {}}
                  type="submit"
                  className="btn-secondary"
                  block
                  label={shared.save}
                  labelClass="px-4 fsize-16 font-medium text-white"
                  disabled={!dirty || !isValid || !!CRFileFormatErrMsg}
                />
              </div>
            )}
          </div>
        </div>
      </form>
      <Modal
        open={isOpenCommissionModal}
        handleClose={() => setIsOpenCommissionModal(false)}
        title={serviceProviders.editServiceProvider.confirmChangeCommissionTitle}
        contentClass="mb-5 pb-2 text-primary"
        // content={<span>{serviceProviders.editServiceProvider[ values.commission_type === 'fixed_fees' ? 'confirmChangeFixedFeesCommissionMessage' : 'confirmChangePercentageCommissionMessage']}</span>}
        content={
          <span>
            {serviceProviders.editServiceProvider[ 
              providerDetails?.commission_type === 'fixed_fees' && values.commission_type === 'percentage' 
              ? 'confirmChangePercentageCommissionMessage'
              : providerDetails?.commission_type === 'percentage' && values.commission_type === 'fixed_fees' 
              ?'confirmChangeFixedFeesCommissionMessage'
              : providerDetails?.commission_type === 'fixed_fees' && providerDetails?.commission_fixed_fees !== values.commission_fixed_fees && values.commission_type === 'fixed_fees'
              ? 'confirmChangeFixedFeesCommissionMessage'
              : providerDetails?.commission_type === 'percentage' && providerDetails?.mimars_commission !== values.mimars_commission && values.commission_type === 'percentage'
              ? 'confirmChangePercentageCommissionValueMessage'
              : 'confirmChangePercentageCommissionValueMessage'
            ]}
          </span>
        }
        confirmBtnTxt={shared.confirm}
        maxWidth="sm"
        cancelBtnTxt={shared.cancel}
        isContentText={false}
        handleConfirm={()=>handleEditProvider()}
        handleCancel={() => setIsOpenCommissionModal(false)}
        modalClass="delete-services-modal"
        actionsClass="container px-4"
        confirmBtnClass="btn-secondary fsize-14 text-white py-2 "
        cancelBtnClass="fsize-14 py-2"
      />
    </div>
  );
};

export default AddEditServiceProviders;
